import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, useMemo, useState, VFC } from 'react';
import styled from 'styled-components';

import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Environment } from 'dashboard/models/environment';
import WorkflowEnvironmentsConfiguration from 'dashboard/views/DashboardWorkflowsShowView/WorkflowEnvironmentsConfiguration';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

const LineDivider = styled.div`
  padding-top: ${Spacing[5]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

interface WorkflowPublishModalProps {
  activeEnvironmentIds: string[];
  availableEnvironments: Environment[];
  description: string;
  initialPercentage: number;
  isInRollout: boolean;
  inactive: boolean;
  handleClose: () => void;
  handleConfirm: (rolloutPercentage: number, environmentIds: string[], description: string) => void;
  workflowName: string;
}

interface WorkflowVersionData {
  description: string;
}

const WorkflowPublishModal: VFC<WorkflowPublishModalProps> = ({
  handleClose,
  handleConfirm,
  activeEnvironmentIds,
  availableEnvironments,
  description,
  initialPercentage,
  isInRollout,
  inactive,
  workflowName,
}) => {
  const activeProductionEnvironmentIds = useMemo(
    () =>
      availableEnvironments
        .filter(
          (env) =>
            env.type === ApiModel.EnvironmentProduction && activeEnvironmentIds.includes(env.id),
        )
        .map((env) => env.id),
    [availableEnvironments, activeEnvironmentIds],
  );

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const hasWritePermission = usePermission(Permission.WorkflowWrite);
  const [isStagingRollout, { toggle: toggleIsStagingRollout }] = useToggle(isInRollout);
  const [rolloutPercentage, setRolloutPercentage] = useState<number>(initialPercentage);
  const [selectedEnvironmentIds, setSelectedEnvironmentIds] = useState<Set<string>>(
    new Set(activeEnvironmentIds),
  );
  const handleSelection = (value: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set<string>(selectedEnvironmentIds);
    if (e.target.checked) {
      newSelected.add(value);
    } else {
      newSelected.delete(value);
    }
    setSelectedEnvironmentIds(newSelected);
  };
  const handleSelectionAll = () => {
    setSelectedEnvironmentIds(new Set(availableEnvironments.map((env) => env.id)));
  };
  const { headerText, confirmButtonText, errors } = useMemo(() => {
    const headerText = isInRollout ? 'Stage Roll-out' : 'Publish Workflow';
    const confirmButtonText =
      isInRollout && rolloutPercentage === 0
        ? 'Roll back'
        : !isInRollout && !isStagingRollout
          ? 'Publish'
          : 'Roll out';
    const errors = {
      ...(selectedEnvironmentIds.size === 0 && {
        environmentSelected: 'Please select at least 1 environment.',
      }),
      ...(!isInRollout &&
        rolloutPercentage === 0 && {
          rollout: 'Cannot set percentage to 0 when there is no active roll-out.',
        }),
    };

    return { headerText, confirmButtonText, errors };
  }, [isInRollout, isStagingRollout, selectedEnvironmentIds, rolloutPercentage]);
  const {
    formState: { isSubmitting },
    handleSubmit,
    register,
  } = useFormSecurely<WorkflowVersionData>({
    defaultValues: {
      description,
    },
  });
  const submit = (data: WorkflowVersionData) => {
    const uncheckedProductionEnvironments = activeProductionEnvironmentIds.filter(
      (id) => !selectedEnvironmentIds.has(id),
    );

    if (uncheckedProductionEnvironments.length > 0 && !showConfirmationModal) {
      setShowConfirmationModal(true);
      return;
    }

    const percentage = isStagingRollout ? rolloutPercentage : 100;

    handleConfirm(percentage, Array.from(selectedEnvironmentIds), data.description);
  };

  return (
    <>
      {!showConfirmationModal && (
        <ModalContainer>
          <ModalHeader onClose={handleClose}>{headerText}</ModalHeader>
          <ModalBody>
            <LineDivider />
            <Form>
              <WorkflowEnvironmentsConfiguration
                editable={hasWritePermission}
                activeEnvironmentIds={inactive ? [] : activeEnvironmentIds}
                availableEnvironments={availableEnvironments}
                selected={selectedEnvironmentIds}
                rolloutPercentage={rolloutPercentage}
                disableSlider={false}
                canToggleRollout={!isInRollout}
                rolloutToggled={isStagingRollout}
                errors={errors}
                setRolloutPercentage={setRolloutPercentage}
                onSelect={handleSelection}
                onSelectAll={handleSelectionAll}
                toggleRollout={toggleIsStagingRollout}
              />
              {isInRollout && rolloutPercentage === 0 && (
                <>
                  <Spacer height={1} />
                  <Paragraph>
                    Revert to previous published version and archive the current version. If there
                    is no previous published version, a new draft will be created off the current
                    version.
                  </Paragraph>
                </>
              )}
              {!isInRollout && (
                <>
                  <Spacer height={3} />
                  <Label size='lg' htmlFor='workflow-publish-modal__description' weight='bold'>
                    Description
                  </Label>
                  <Spacer height={1} />
                  <TextArea
                    id='workflow-publish-modal__description'
                    placeholder='Optional description for this version...'
                    {...register('description')}
                  />
                </>
              )}
            </Form>
            <Spacer height={3} />
            <LineDivider />
            <HStack justify='flex-end' spacing={Spacing[2]}>
              <Button
                id='workflow-publish-modal__btn-cancel'
                variant='basic'
                width='auto'
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                disabled={!hasWritePermission || !!errors.environmentSelected || !!errors.rollout}
                id='workflow-publish-modal__btn-confirm'
                isPending={isSubmitting}
                variant='primary'
                width='auto'
                onClick={handleSubmit(submit)}
              >
                {confirmButtonText}
              </Button>
            </HStack>
          </ModalBody>
        </ModalContainer>
      )}
      {showConfirmationModal && (
        <ConfirmationModal
          cancelAction={() => setShowConfirmationModal(false)}
          confirmAction={() => {
            const percentage = isStagingRollout ? rolloutPercentage : 100;
            handleConfirm(percentage, Array.from(selectedEnvironmentIds), description);
            setShowConfirmationModal(false);
          }}
          header='Deactivate Live Workflow'
          confirmActionText={isStagingRollout ? 'Roll out' : 'Publish'}
          confirmButtonType='danger'
          allowConfirm={inputValue === workflowName}
        >
          <Paragraph>
            <strong>{workflowName}</strong> will be deactivated for unchecked production
            environments. To confirm and {isStagingRollout ? 'roll out' : 'publish'}, type in{' '}
            <strong>{workflowName}</strong>
          </Paragraph>
          <Spacer height={2} />
          <Input
            id='workflow-publish-modal__confirmation-input'
            value={inputValue}
            onChange={handleInputChange}
            role='textbox'
          />
        </ConfirmationModal>
      )}
    </>
  );
};

export default WorkflowPublishModal;
